import { colorForShare } from "@/components/assessment/results/RiskShareBarList";
import { HAZARD_LABELS, hazardKeyLabel, hazardTypeOfKey } from "@/lib/results";
import { describeMissing, type HazardShare, type ReportModel } from "@/lib/report";
import { BASIS_TEXT } from "@/lib/report-basis";
import { HorizonLabel, SectionHeading } from "./ReportPage";

/**
 * The StatCard shape docs/design-guidelines.md marks Resolved ("don't invent a
 * third variant") — classes copied verbatim from the shared StatCard, now at
 * client/src/components/ui/stat-card.tsx (moved out of AdminStats.tsx on
 * 2026-09-27).
 *
 * That shared component's `value` now accepts `string | number`, so this local
 * copy is redundant with it and could be replaced by an import. Deliberately
 * not done yet: this tree has PDF-pagination constraints of its own (see
 * design-guidelines' note on the report "paper" pattern), so swapping it is
 * its own change, not a side effect of another one.
 */
function KpiTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-400">{label}</p>
      <p className="mt-2 text-3xl font-bold">{value}</p>
    </div>
  );
}

/** Colored bar + "pct% (x of y)"; "—" plus the reason when nothing is computed (never 0 for pending). */
function ShareCell({ share }: { share: HazardShare }) {
  if (share.pct === null) return <span className="text-muted-foreground">— {describeMissing(share.counts)}</span>;

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-16 shrink-0 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full" style={{ width: `${share.pct}%`, background: colorForShare(share.pct) }} />
      </div>
      <span>
        {share.pct}% ({share.high} of {share.total})
      </span>
    </div>
  );
}

export function ReportSummary({ model }: { model: ReportModel }) {
  const showHorizonLabels = model.sections.length > 1;
  const caveatHazards = new Set(model.caveats.flatMap((c) => c.hazards));

  return (
    // Block + margin spacing, not flex: its children are the break-inside-avoid
    // blocks, and Chromium ignores that on flex items (see ReportPage).
    <div className="space-y-6">
      <section className="break-inside-avoid">
        <SectionHeading>Executive summary</SectionHeading>
        <div className="mt-3 space-y-2 text-sm leading-relaxed">
          {model.summary.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </section>

      <section className="break-inside-avoid">
        <div className="flex gap-4">
          {model.sections.map((s) => (
            <div key={s.horizon} className="flex min-w-0 flex-1 flex-col gap-3">
              {showHorizonLabels && <HorizonLabel>{s.label}</HorizonLabel>}
              <KpiTile label="Assets assessed" value={`${s.kpis.assetsAssessed} of ${s.kpis.assetsTotal}`} />
              <KpiTile label="Business-critical assets" value={`${s.kpis.businessCritical} of ${s.kpis.assetsTotal}`} />
              <KpiTile label="Hazards assessed" value={`${s.kpis.hazardsAssessed} of ${s.kpis.hazardsInScope}`} />
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">{BASIS_TEXT.businessCritical}</p>
      </section>

      <section className="break-inside-avoid">
        <SectionHeading>Assets at high risk, by hazard</SectionHeading>
        <p className="mb-3 mt-1 text-xs text-muted-foreground">{BASIS_TEXT.shareNote}</p>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="p-2 font-medium">Hazard</th>
              {model.sections.map((s) => (
                <th key={s.horizon} className="p-2 font-medium">
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {model.hazardKeys.map((key) => (
              <tr key={key} className="break-inside-avoid border-t border-border">
                <td className="p-2 font-medium">
                  {hazardKeyLabel(key)}
                  {caveatHazards.has(hazardTypeOfKey(key)) && " †"}
                </td>
                {model.sections.map((s) => (
                  <td key={s.horizon} className="p-2">
                    <ShareCell share={s.perHazard.find((p) => p.key === key)!} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {model.caveats.map((c) => (
          <p key={c.text} className="mt-2 text-[11px] text-muted-foreground">
            † {c.hazards.map((h) => HAZARD_LABELS[h]).join(", ")}: {c.text}
          </p>
        ))}
      </section>
    </div>
  );
}
